import { useCallback, useMemo, useState } from 'react';
import { FieldValues, useFormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom-v5-compat';

import InquiryPreview, {
  InquiryPreviewHeader,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview';
import { useSimulateAutoSaveForm } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/useSimulateAutoSaveForm';
import { initialSimulateStubsAndPresets } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import CreateInquiryFieldsPrefill from 'dashboard/components/CreateInquiry/CreateInquiryFieldsPrefill';
import CreateInquiryGeneralInfoSimulate from 'dashboard/components/CreateInquiry/CreateInquiryGeneralInfoSimulate';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import TabbedSubmitModal from 'dashboard/components/TabbedSubmitModal';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { VerificationTemplateVersionStubs } from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import { FieldValue } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface InquiryTemplatePreviewProps {
  inquiry: Inquiry | undefined;
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  accountTypesFilterOptions: AccountTypesFilterOption[] | undefined;
  workflowVersionMap: WorkflowVersionMap;
  verificationTemplateVersions: Record<string, VerificationTemplateVersion>;
  isLoadingInquiry: boolean;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  handleSaveVerificationTemplateVersionStubs: (
    inquiryId: string,
    verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
    fieldStubs: Record<string, FieldValue>,
  ) => void;
  handleCreateSimulateInquiry: (props: {
    inquiryTemplateId: string;
    inquiryTemplateVersionId: string;
    referenceId?: string;
    accountTypeId?: string;
    fields?: Record<string, any>;
  }) => Promise<void>;
  handleSkipToStep: (stepName: string, inquiryId: string) => Promise<void>;
  handleViewInquiry: () => void;
  handleLoadInquiry: (inquiryId: string, abortSignal?: AbortSignal) => Promise<void>;
}

export const InquiryPreviewFromTemplate = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  inquiry,
  accountTypesFilterOptions,
  workflowVersionMap,
  isLoadingInquiry,
  verificationTemplateVersions,
  trackEvent,
  handleCreateSimulateInquiry,
  handleSaveVerificationTemplateVersionStubs,
  handleSkipToStep,
  handleViewInquiry,
  handleLoadInquiry,
}: InquiryTemplatePreviewProps) => {
  const [showPrefillModal, { enable: openPrefillModal, disable: hidePrefillModal }] = useToggle();

  // Stores the name of the account type associated with the created inquiry
  const [usedAccountTypeName, setUsedAccountTypeName] = useState<string | null>(null);

  const [isAdvancedMode, { toggle: toggleAdvancedMode }] = useToggle(false);

  const {
    reset: simulateFormReset,
    setValue: simulateFormSetValue,
    formValues: simulateStubsFormValues,
  } = useSimulateAutoSaveForm({
    inquiry: inquiry ?? null,
    verificationTemplateVersions,
    isAdvancedMode,
    handleSaveVerificationTemplateVersionStubs,
  });

  // By default, we want to always use the legacy account type.
  const initialAccountType =
    accountTypesFilterOptions?.find((option) => option['is-legacy-account-type']) ??
    accountTypesFilterOptions?.[0];
  const {
    handleSubmit,
    control,
    watch,
    reset: resetPrefillForm,
  } = useFormSecurely<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      accountTypeId: initialAccountType?.id,
    },
  });
  const { isSubmitting } = useFormState({
    control,
  });

  const abortController = useMemo(() => new AbortController(), [inquiry?.id]);

  /**
   * Handles creating the simulate inquiry on first load and on restart.
   * On first load, the stubs and presets are initialized in InquiryTemplateSimulateStubbing
   */
  const createSimulateInquiry = useCallback(async () => {
    handleCreateSimulateInquiry({
      inquiryTemplateId: inquiryTemplate.id,
      inquiryTemplateVersionId: inquiryTemplateVersion.id,
      accountTypeId: watch('accountTypeId'),
      referenceId: watch('referenceId'),
      fields: watch('fields'),
    });
    setUsedAccountTypeName(
      accountTypesFilterOptions?.find((option) => option.id === watch('accountTypeId'))?.name ??
        null,
    );
  }, [handleCreateSimulateInquiry, inquiryTemplate.id, inquiryTemplateVersion.id, watch]);

  /**
   * Handles recreating the simulate inquiry when restarting the preview.
   * Takes in options for which settings should be preserved when restarting.
   */
  const recreateSimulateInquiryForRestart = useCallback(
    async (options: { resetStubsAndPresets: boolean; resetPrefills: boolean }) => {
      abortController.abort();

      // Reset the prefill form before creating the new inquiry since the inquiry will read from the fields
      if (options.resetPrefills) {
        resetPrefillForm();
      }

      // Create the new inquiry
      await createSimulateInquiry();

      // After the inquiry has been created, initialize the stubs and presets
      if (options.resetStubsAndPresets) {
        const initialStubsAndPresets = initialSimulateStubsAndPresets(
          verificationTemplateVersions,
          {
            isAdvancedMode,
          },
        );
        simulateFormReset(initialStubsAndPresets);
      }
    },
    [
      abortController,
      createSimulateInquiry,
      isAdvancedMode,
      resetPrefillForm,
      verificationTemplateVersions,
    ],
  );

  const handlePrefillRestartAndClosePrefillModal = () => {
    trackEvent('Flow Editor - Preview With Prefill');
    recreateSimulateInquiryForRestart({ resetStubsAndPresets: true, resetPrefills: false });
    hidePrefillModal();
  };

  const navigate = useNavigate();

  return (
    <>
      <ModalWrapper isOpen={showPrefillModal} handleCloseModal={hidePrefillModal}>
        <TabbedSubmitModal
          headerText='Prefill a new Inquiry for preview'
          submitText='Prefill and restart'
          tabContents={[
            {
              name: 'General',
              contents: (
                <CreateInquiryGeneralInfoSimulate
                  accountTypeBasics={accountTypesFilterOptions || []}
                  submitting={isSubmitting}
                  control={control}
                />
              ),
            },
            {
              name: 'Fields (optional)',
              contents: (
                <CreateInquiryFieldsPrefill
                  fieldSchema={inquiryTemplateVersion.attributes['field-schema']}
                  isLoading={false}
                  submitting={isSubmitting}
                  control={control}
                />
              ),
            },
          ]}
          disableSubmit={false}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit(handlePrefillRestartAndClosePrefillModal)}
          handleCancel={hidePrefillModal}
        />
      </ModalWrapper>
      <InquiryPreview
        inquiryTemplate={inquiryTemplate}
        inquiryTemplateVersion={inquiryTemplateVersion}
        inquiry={inquiry}
        workflowVersionMap={workflowVersionMap}
        isLoadingInquiry={isLoadingInquiry}
        verificationTemplateVersions={verificationTemplateVersions}
        isAdvancedMode={isAdvancedMode}
        toggleAdvancedMode={toggleAdvancedMode}
        simulateStubsFormValues={simulateStubsFormValues}
        setSimulateStubsFormValues={simulateFormSetValue}
        trackEvent={trackEvent}
        handleInitializeSimulateInquiry={createSimulateInquiry}
        handleSkipToStep={handleSkipToStep}
        handleLoadInquiry={(inquiryId) => handleLoadInquiry(inquiryId, abortController.signal)}
        header={
          <InquiryPreviewHeader
            inquiryTemplate={inquiryTemplate}
            inquiryTemplateVersion={inquiryTemplateVersion}
            inquiry={inquiry}
            // Only render the account type name in the header if there is more than one for the org
            accountTypeName={
              (accountTypesFilterOptions?.length ?? 0) > 0 ? usedAccountTypeName : null
            }
            onBackClick={() =>
              navigate(
                `/dashboard/inquiry-templates/${inquiryTemplate.id}/${inquiryTemplateVersion.id}/flow`,
              )
            }
            onRestart={(restartOptions) => {
              trackEvent('Flow Editor - Preview Restart');
              recreateSimulateInquiryForRestart(restartOptions);
            }}
            onPrefill={openPrefillModal}
            onViewInquiry={handleViewInquiry}
          />
        }
      />
    </>
  );
};
